<template>
  <section class="container-fluid d-flex mt-5 pt-5 justify-content-center px-3">
    <!-- Loading State -->
    <div v-if="isLoading" class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="col-lg-6">
        <div class="skeleton-image"></div>
      </div>
      <div class="col-lg-6 my-4 my-lg-0">
        <div class="skeleton-content">
          <div class="skeleton-title"></div>
          <div class="skeleton-tags d-flex gap-2">
            <div class="skeleton-tag"></div>
            <div class="skeleton-tag"></div>
          </div>
          <div class="skeleton-description"></div>
          <div class="skeleton-specifics d-flex gap-4">
            <div class="skeleton-specific" v-for="i in 4" :key="i"></div>
          </div>
          <div class="skeleton-price"></div>
          <div class="skeleton-button"></div>
        </div>
      </div>
    </div>

    <!-- Product Content -->
    <div v-else-if="product" class="container row col-12 pb-5 d-flex justify-content-center mt-5 pt-5">
      <div class="col-lg-6 px-0 pe-lg-4 product-img-wrapper d-flex flex-column">
        <!-- Main Image -->
        <div 
          class="zoom-container mb-3"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
          ref="zoomContainer"
        >
          <img
            :src="currentImage"
            :alt="product.name"
            class="zoom-image"
            :style="zoomStyle"
            ref="zoomImage"
          />
        </div>
        
        <!-- Thumbnails Row -->
        <div v-if="hasMultipleImages" class="thumbnails-row d-flex gap-2 justify-content-center">
          <button 
            v-for="(image, index) in productImages" 
            :key="index"
            class="thumbnail-btn p-2"
            :class="{ active: currentImage === image }"
            @click="currentImage = image"
          >
            <img :src="image" :alt="`${product.name} thumbnail ${index + 1}`" class="thumbnail-img">
          </button>
        </div>
      </div>
      <div class="col-lg-6 my-4 my-lg-0 px-0">
        <!-- edit btn -->
        <router-link v-if="isAdmin" :to="`/admin/productos/editar/${product.id}`" class="btn btn-primary mb-3">
          <i class="fas fa-edit me-2"></i>
          Editar Producto
        </router-link>
        <h1 class="display-4 fw-bold">{{ product.name }}</h1>
        <div class="d-flex tags gap-2">
          <span class="tag">{{ formatType(product.type) }}</span>
          <span class="tag">{{ product.brand }}</span>
        </div>
        <div v-if="product.description" class="mt-4 fs-5">
          <div class="description-wrapper">
            <div v-html="formattedTruncatedDescription" class="d-inline"></div>
            <div v-if="product.description.length > 300" class="d-inline"><span class="text-muted fw-light">...</span>
              <button 
                @click="showFullDescription"
                class="btn btn-link text-white text-decoration-underline p-0 d-inline"
              >
                Leer más
              </button>
            </div>
          </div>
        </div>
        <p v-else class="mt-4 fs-5 text-muted fst-italic">
          <i class="fas fa-info-circle me-2"></i>
          Descripción no disponible
        </p>
        <div class="specifics d-flex flex-wrap align-items-start pb-0 mt-2">
          <div 
            class="specific d-flex flex-column align-items-center justify-content-center"
            :class="{ 'specific-disabled': !product.flowering_time }"
          >
            <div class="specific__img-wrapper">
              <img src="@/assets/icons/cannabis.svg" alt="icono de cannabis" class="img-fluid" />
            </div>
            <h4 class="fw-semibold fs-7 mb-0">Floración</h4>
            <p v-if="product.flowering_time" class="fs-7">{{ product.flowering_time }}</p>
            <p v-else class="fs-7 text-muted">N/D</p>
          </div>
          <div 
            class="specific d-flex flex-column align-items-center justify-content-center"
            :class="{ 'specific-disabled': !product.thc }"
          >
            <div class="specific__img-wrapper">
              <img src="@/assets/icons/bolt.svg" alt="icono de rayo" class="img-fluid" />
            </div>
            <h4 class="fw-semibold fs-7 mb-0">THC</h4>
            <p v-if="product.thc" class="fs-7">{{ product.thc }}</p>
            <p v-else class="fs-7 text-muted">N/D</p>
          </div>
          <div 
            class="specific d-flex flex-column align-items-center justify-content-center"
            :class="{ 'specific-disabled': !product.cbd }"
          >
            <div class="specific__img-wrapper">
              <img src="@/assets/icons/medical-drug.svg" alt="icono de pastilla" class="img-fluid" />
            </div>
            <h4 class="fw-semibold fs-7 mb-0">CBD</h4>
            <p v-if="product.cbd" class="fs-7">{{ product.cbd }}</p>
            <p v-else class="fs-7 text-muted">N/D</p>
          </div>
          <div class="specific d-flex flex-column align-items-center justify-content-center">
            <div class="specific__img-wrapper">
              <img
                src="@/assets/icons/female.svg"
                alt="icono de genero femenino"
                class="img-fluid"
              />
            </div>
            <h4 class="fw-semibold fs-7 mb-0">Feminizada</h4>
            <p class="fs-7">Si</p>
          </div>
          <div 
            class="specific d-flex flex-column align-items-center justify-content-center"
            :class="{ 'specific-disabled': !product.flavors }"
          >
            <div class="specific__img-wrapper mb-2">
              <img src="@/assets/icons/ice-cream.svg" alt="icono de helado" class="img-fluid" />
            </div>
            <h4 class="fw-semibold fs-7 mb-0">Sabor</h4>
            <div v-if="product.flavors" class="flavors-list">
              <span 
                    class="text-white fs-7 fw-light"
              >
                {{ flavorsList[0] }}
              </span>
            </div>
            <p v-else class="fs-7 text-muted">N/D</p>
          </div>

        </div>
        <div v-if="isUserAuthenticated" class="price mt-2">
          <div class="d-flex justify-content-start gap-5 align-items-center mb-3">
            <div class=" mb-3">
              <div class="form-group p-0">
                <label class="form-label mb-1 fw-light fs-5">Presentación</label>
                <select 
                  v-model="selectedBlister" 
                  class="form-select form-select-sm py-2 px-3"
                  :class="{ 'disabled': !product.blisters.length }"
                >
                  <option 
                    v-for="blister in product.blisters" 
                    :key="blister.id" 
                    :value="blister"
                    :disabled="blister.stock === 0"
                  >
                    {{ blister.seed_count }} Semillas
                    <span>
                      ${{ formatPrice(blister.price) }}
                    </span>
                    {{ blister.stock === 0 ? '(Sin stock)' : '' }}
                  </option>
                </select>
            </div>
            </div>
            <div>
              <h4 class=" fs-5 mb-0" v-if="selectedBlister && selectedBlister.price > 0">Precio</h4>
              <div v-if="selectedBlister && selectedBlister.price > 0" class="d-flex align-items-baseline">
                
                <div class="price-container">
                  <!-- Show original price strikethrough if there's a discount -->
                  <p v-if="selectedBlister.discount > 0" class="old-price mb-1">${{ formatPrice(selectedBlister.price) }}</p>
                  
                  <!-- Show final price (discounted if applicable) -->
                  <p class="fs-3 fw-bold mb-0" :class="{ 'discounted-price': selectedBlister.discount > 0 }">
                    ${{ formatPrice(selectedBlister.final_price || selectedBlister.price) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Stock info section below price and presentation -->
          <div v-if="selectedBlister" class="stock-info mt-3 mb-3 d-flex align-items-center gap-2">
            <i class="fas fa-box text-white"></i>
            <span v-if="selectedBlister.stock > 0" class="fs-6 text-white fw-normal">
              {{ selectedBlister.stock }} Unidades disponibles
            </span>
            <span v-else class="fs-6 text-danger">
              Sin stock
            </span>
          </div>
          
          <div class="d-flex gap-3 align-items-center">
            <button 
              class="btn btn-light px-5 py-3" 
              @click="handleAddToCart"
              :disabled="!selectedBlister || selectedBlister.stock === 0 || selectedBlister.price <= 0"
            >
              <i class="fas fa-shopping-cart me-2"></i>
              Añadir al Carrito
            </button>
          </div>
        </div>
        <div v-else class="login-required-section mt-4 p-4">
          <div class="alert alert-info">
            <h4 class="alert-heading mb-3"><i class="fas fa-lock me-2"></i>Precios exclusivos para usuarios registrados</h4>
            <p>Para ver los precios y realizar compras, por favor inicia sesión o regístrate en nuestra plataforma.</p>
            <div class="d-flex gap-3 mt-3">
              <router-link to="/login" class="btn btn-primary">
                <i class="fas fa-sign-in-alt me-2"></i>Iniciar Sesión
              </router-link>
              <router-link to="/crear-cuenta" class="btn btn-outline-light">
                <i class="fas fa-user-plus me-2"></i>Registrarse
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 mt-5 px-0">
        <div class="section-btns d-flex gap-lg-4 gap-3">
          <button
            class="btn btn-transparent p-0 fs-lg-3 fs-5 fw-semibold"
            :class="{ active: sectionActive === 'technical-sheet' }"
            @click="sectionActive = 'technical-sheet'"
          >
            Ficha Técnica
          </button>
          <button
            class="btn btn-transparent p-0 fs-lg-3 fs-5 fw-semibold"
            :class="{ active: sectionActive === 'description' }"
            @click="sectionActive = 'description'"
          >
            Descripción
          </button>
          <button
            v-if="product.brand_info && product.brand_info.description"
            class="btn btn-transparent p-0 fs-lg-3 fs-5 fw-semibold"
            :class="{ active: sectionActive === 'brand-info' }"
            @click="sectionActive = 'brand-info'"
          >
            Info del Banco
          </button>
        </div>
        <div v-if="sectionActive === 'technical-sheet'">
          <table class="table mt-4">
            <tbody>
              <template v-for="(data, key) in technicalData" :key="key">
                <tr v-if="data.value" class="technical-row">
                  <td class="fw-bold">
                    <i :class="data.icon" class="me-2 text-primary"></i>
                    {{ key }}
                  </td>
                  <td>{{ data.value }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="sectionActive === 'description'" class="mt-4 description-content">
          <div v-if="product.description" class="brand-info-section p-4" v-html="formattedFullDescription"></div>
          <p v-else class="text-muted">No hay descripción disponible para este producto.</p>
        </div>
        <div v-if="sectionActive === 'brand-info'" class="mt-4">
          <div
            v-if="product.brand_info && product.brand_info.description"
            class="brand-info-section p-4"
          >
            <div class="d-flex align-items-center mb-4 gap-3">
              <div class="brand-logo-wrapper">
                <img 
                  :src="product.brand_info.image" 
                  :alt="product.brand + ' logo'"
                  class="brand-logo"
                />
              </div>
              <div>
                <h4 class="fw-bold mb-1">{{ product.brand }}</h4>
                <span class="text-muted">
                  <i class="fas fa-flag me-1"></i>
                  {{ product.brand_info.is_national ? 'Banco Nacional' : 'Banco Internacional' }}
                </span>
              </div>
            </div>
            <div class="brand-description">
              <div v-html="formattedFullBrandDescription"></div>
            </div>
          </div>
          <div v-else class="mt-4">
            <p class="text-muted">No hay información disponible sobre este banco.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Product Found -->
    <div v-else class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="text-center">
        <i class="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
        <h3>Producto no encontrado</h3>
        <p class="text-muted">El producto que buscas no existe o no está disponible.</p>
        <router-link to="/productos" class="btn btn-primary mt-3">
          <i class="fas fa-arrow-left me-2"></i>
          Volver a Productos
        </router-link>
      </div>
    </div>
  </section>
  <!-- Related Products Section -->
  <section v-if="relatedProducts.length > 0" class="container mt-5 pb-5 px-3">
    <h2 class="text-white mb-4">Productos Relacionados</h2>
    <div class="row g-4 d-flex justify-content-center">
      <div v-for="product in relatedProducts" :key="product.id" class="col-12 col-md-3">
        <ProductCard :product="product" class="related-product-card" />
      </div>
    </div>
    <div class="d-flex justify-content-center flex-column align-items-center">
      <p class="text-white mt-5 fw-light">¿Quieres ver todas las genéticas?
      </p>
      <router-link to="/productos" class="btn btn-primary mt-2">
        <i class="fas fa-store me-2"></i>
        Catálogo
      </router-link>
    </div>
  </section>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from "vue-toastification";
import { getProduct, getProducts } from "@/services/products";
import ProductCard from "@/components/ProductCard.vue";
import { formatPrice } from "@/helpers/product";
import { isAuthenticated } from "@/services/auth";

export default {
  name: "ProductDetails",
  components: {
    ProductCard
  },
  setup() {
    const store = useStore();
    const toast = useToast();
    const isAdmin = localStorage.getItem('isAdmin') == 'true';
    const isUserAuthenticated = computed(() => isAuthenticated());
    const product = ref(null);
    const sectionActive = ref('technical-sheet');
    const isLoading = ref(true);
    const relatedProducts = ref([]);
    const selectedBlister = ref(null);

    const formatType = (type) => {
      const types = {
        'fem': 'Feminizada',
        'auto': 'Automática',
        'reg': 'Regular'
      };
      return types[type] || type;
    };

    const handleAddToCart = () => {
      if (!isUserAuthenticated.value) {
        toast.error('Debes iniciar sesión para agregar productos al carrito');
        return;
      }
      
      if (!selectedBlister.value) {
        toast.error('Por favor selecciona una presentación');
        return;
      }
      
      if (selectedBlister.value.stock <= 0) {
        toast.error('No hay stock disponible');
        return;
      }
      
      const cartItem = {
        id: product.value.id,
        blister_id: selectedBlister.value.id,
        name: product.value.name,
        price: parseFloat(selectedBlister.value.final_price || selectedBlister.value.price),
        brand: product.value.brand,
        image: product.value.image,
        quantity: 1
      };

      store.dispatch('addToCart', cartItem);
      toast.success('Producto añadido al carrito');
    };

    const technicalData = computed(() => {
      if (!product.value) return {};
      
      return {
        'Banco': { 
          value: product.value.brand,
          icon: 'fas fa-building'
        },
        'Tipo': { 
          value: formatType(product.value.type),
          icon: 'fas fa-cannabis'
        },
        'Genotipo': { 
          value: product.value.genotype ,
          icon: 'fas fa-dna'
        },
        'Composición': { 
          value: product.value.sativa && product.value.indica ? 
            `${product.value.sativa}% Sativa / ${product.value.indica}% Indica` : null,
          icon: 'fas fa-percentage'
        },
        'Efectos': { 
          value: product.value.effects ,  
          icon: 'fas fa-brain'
        },
        'Sabores': { 
          value: product.value.flavors ,
          icon: 'fas fa-ice-cream'
        },
        'THC': { 
          value: product.value.thc ,
          icon: 'fas fa-bolt'
        },
        'CBD': { 
          value: product.value.cbd ,
          icon: 'fas fa-pills'
        },
        'Tiempo de Floración': { 
          value: product.value.flowering_time,
          icon: 'fas fa-clock'
        },
        'Ciclo Completo': { 
          value: product.value.completed_cicle,
          icon: 'fas fa-clock'
        },
        'Altura Interior': { 
          value: product.value.height_indoor ,
          icon: 'fas fa-ruler-vertical'
        },
        'Altura Exterior': { 
          value: product.value.height_outdoor ,
          icon: 'fas fa-mountain'
        },
        'Producción Interior': { 
          value: product.value.yield_indoor ,
          icon: 'fas fa-warehouse'
        },
        'Producción Exterior': { 
          value: product.value.yield_outdoor ,
          icon: 'fas fa-sun'
        },
        'Semillas por Blíster': { 
          value: product.value.seed_count ,
          icon: 'fas fa-seedling'
        },
        'Stock': { 
          value: product.value.stock ,
          icon: 'fas fa-box'
        }
      };
    });

    const loadRelatedProducts = async () => {
      if (!product.value) return;
      
      try {
        let allProducts = [];
        
          // Si no hay caché, obtener de la API
          const response = await getProducts();
          allProducts = response;
        
        // Filtrar productos relacionados
        relatedProducts.value = allProducts
          .filter(p => 
            p.id !== product.value.id && // Excluir producto actual
            p.brand === product.value.brand).slice(0, 4); // Limitar a 4 productos
      } catch (error) { 
        console.error('Error loading related products:', error);
      }
    };

    // Computed property for truncated description
    const truncatedDescription = computed(() => {
      if (!product.value?.description) return '';
      if (product.value.description.length <= 250) return product.value.description;
      return product.value.description.slice(0, 250) + '...';
    });

    // Method to show full description
    const showFullDescription = () => {
      sectionActive.value = 'description';
      // Scroll to description section smoothly
      document.querySelector('.section-btns').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    };

    const formattedDescription = (text) => {
      if (!text) return '';
      // Convertir saltos de línea en etiquetas <p>
      return text
        .split('\n')
        .filter(p => p.trim()) // Eliminar líneas vacías
        .map(p => `<p class="mb-3">${p.trim()}</p>`)
        .join('');
    };

    const formattedTruncatedDescription = computed(() => {
      if (!product.value?.description) return '';
      let text = product.value.description;
      if (text.length > 300) {
        const truncated = text.substr(0, 300);
        const lastSpace = truncated.lastIndexOf(' ');
        text = truncated.substr(0, lastSpace > 0 ? lastSpace : 300);
      }
      return formattedDescription(text);
    });

    const formattedFullDescription = computed(() => {
      if (!product.value?.description) return '';
      return formattedDescription(product.value.description);
    });

    // Brand info description (bank info)
    const showFullBrandInfo = ref(false);
    const formattedTruncatedBrandDescription = computed(() => {
      const text = product.value?.brand_info?.description;
      if (!text) return '';
      let t = text;
      if (t.length > 220) {
        const truncated = t.substr(0, 220);
        const lastSpace = truncated.lastIndexOf(' ');
        t = truncated.substr(0, lastSpace > 0 ? lastSpace : 220);
      }
      return formattedDescription(t);
    });

    const formattedFullBrandDescription = computed(() => {
      const text = product.value?.brand_info?.description;
      if (!text) return '';
      return formattedDescription(text);
    });

    // Add computed property for flavors list
    const flavorsList = computed(() => {
      if (!product.value?.flavors) return [];
      return product.value.flavors.split(',').map(flavor => flavor.trim());
    });

    // Watch for product changes to set initial blister
    watch(() => product.value, (newProduct) => {
      if (newProduct?.blisters?.length) {
        // Ordenar blisters por cantidad de semillas
        const sortedBlisters = [...newProduct.blisters].sort((a, b) => a.seed_count - b.seed_count);
        // Seleccionar el primer blister con stock
        selectedBlister.value = sortedBlisters.find(b => b.stock > 0) || sortedBlisters[0];
      }
    }, { immediate: true });

    const zoomContainer = ref(null);
    const zoomImage = ref(null);
    const zoomStyle = ref({});

    const handleMouseMove = (e) => {
      const { left, top, width, height } = zoomContainer.value.getBoundingClientRect();
      const x = (e.clientX - left) / width;
      const y = (e.clientY - top) / height;

      zoomStyle.value = {
        transform: 'scale(2)',
        transformOrigin: `${x * 100}% ${y * 100}%`
      };
    };

    const handleMouseLeave = () => {
      zoomStyle.value = {
        transform: 'scale(1)',
        transformOrigin: 'center center'
      };
    };

    const currentImage = ref('');
    const productImages = computed(() => {
      if (!product.value) return [];
      return [
        product.value.image,
        product.value.image_2,
        product.value.image_3
      ].filter(img => img); // Filtrar valores nulos o undefined
    });

    const hasMultipleImages = computed(() => productImages.value.length > 1);

    // Añadir al mounted o watch del producto
    watch(() => product.value, (newProduct) => {
      if (newProduct) {
        currentImage.value = newProduct.image;
      }
    }, { immediate: true });

    return {
      product,
      sectionActive,
      formatPrice,
      handleAddToCart,
      isAdmin,
      isUserAuthenticated,
      isLoading,
      technicalData,
      relatedProducts,
      loadRelatedProducts,
      ProductCard,
      truncatedDescription,
      showFullDescription,
      formattedTruncatedDescription,
      formattedFullDescription,
  // bank info
  showFullBrandInfo,
  formattedTruncatedBrandDescription,
  formattedFullBrandDescription,
  toggleBrandInfo: () => { showFullBrandInfo.value = !showFullBrandInfo.value; },
      flavorsList,
      selectedBlister,
      formatType,
      zoomContainer,
      zoomImage,
      zoomStyle,
      handleMouseMove,
      handleMouseLeave,
      currentImage,
      productImages,
      hasMultipleImages,
    };
  },
  async mounted() {
    try {
      const productId = this.$route.params.id;
      this.isLoading = true;
      this.product = await getProduct(productId);
      await this.loadRelatedProducts();
      console.log(this.product);
    } catch (error) {
      console.error('Error loading product:', error);
      // Manejar el error apropiadamente
    } finally {
      this.isLoading = false;
    }
    
    // seteo el titulo de la página
    document.title = "Detalles del Producto - " + this.product.name;
  },
  watch: {
    '$route.params.id': async function(newId) {
      this.isLoading = true;
      try {
        this.product = await getProduct(newId);
        await this.loadRelatedProducts();
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Styles remain unchanged */
.tag {
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
}

.product-img-wrapper {
  max-height: 700px;
  display: flex;
  align-items: end;
  justify-content: center;
  position: relative;
}

.product-img-wrapper::after{
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100%;
  background: linear-gradient(to right, transparent, var(--bg-color));
  pointer-events: none; /* Ensure the gradient doesn't interfere with hover */
  z-index: 1;
}

.specifics {
  margin-top: 30px;
  display: flex;
  gap: 0.75rem;
  width: 100%;
  overflow-x: auto;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  padding-bottom: 1rem;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.specifics::-webkit-scrollbar {
  display: none;
}

.specific {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.specific__img-wrapper {
  width: 32px;
  height: 32px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.specific h4 {
  font-size: 0.8rem;
  text-align: center;
  margin-bottom: 0.25rem;
}

.specific p {
  font-size: 0.75rem;
  text-align: center;
  margin: 0;
}

.specific-disabled {
  opacity: 0.5;
  filter: grayscale(1);
}

.specific-disabled:hover {
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.specific__img-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  transition: all 0.3s ease;
}

.text-muted {
  opacity: 0.6;
}

.fst-italic {
  font-style: italic;
}

.section-btns .active {
  color: var(--white-color-2);
  border-radius: 10px;
  border: none;
}

.table {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

tr {
  border: 1px solid transparent;
}

/* Skeleton Styles */
.skeleton-image {
  width: 100%;
  height: 600px;
  background: var(--black-color-2);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 20px;
}

.skeleton-content {
  padding: 1rem;
}

.skeleton-title {
  height: 48px;
  width: 80%;
  background: var(--black-color-2);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.skeleton-tags {
  margin: 1rem 0;
}

.skeleton-tag {
  height: 24px;
  width: 80px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-description {
  height: 100px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-specifics {
  margin: 2rem 0;
}

.skeleton-specific {
  height: 80px;
  width: 80px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

.skeleton-price {
  height: 64px;
  width: 40%;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-button {
  height: 48px;
  width: 200px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-image {
    height: 300px;
  }
  
  .skeleton-specific {
    height: 60px;
    width: 60px;
  }
  .specific{
    min-width: 40px;
  }
}

/* Related Products Styles */
.related-product-card {
  background: var(--black-color-3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.related-product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.card-img-wrapper {
  aspect-ratio: 1;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-img-top {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1rem;
  transition: transform 0.3s ease;
}

.related-product-card:hover .card-img-top {
  transform: scale(1.05);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--white-color);
}

.price {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--white-color);
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .card-title {
    font-size: 0.9rem;
  }

  .price {
    font-size: 1rem;
  }

  .btn {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.table tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

.technical-row {
  transition: all 0.2s ease;
}

.technical-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.technical-row i {
  width: 20px;
  text-align: center;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  vertical-align: middle;
}

.btn-link {
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}

.btn-link:hover {
  text-decoration: underline;
}

.description-content :deep(p) {
  margin-bottom: 1rem;
}

.description-content :deep(p:last-child) {
  margin-bottom: 0;
}

.flavors-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.flavor-tag {
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  white-space: nowrap;
}

.blister-selector {
  background: var(--black-color-2);
  padding: 1rem;
  border-radius: 8px;
}

.form-select {
  background-color: var(--black-color-3);
  color: var(--white-color);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem rgba(var(--primary-color-rgb), 0.25);
}

.blister-options {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.blister-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: var(--black-color-3);
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  color: white;
}

.blister-option:hover:not(.disabled) {
  background: var(--black-color-2);
  transform: translateY(-2px);
}

.blister-option.active {
  border-color: var(--primary-color);
  background: var(--black-color-2);
}

.blister-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(0.5);
}

.blister-option__content {
  flex: 1;
}

.blister-option__header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.seed-count {
  font-weight: 500;
  font-size: 0.9rem;
}

.stock {
  font-size: 0.8rem;
  opacity: 0.7;
}

.blister-option__price {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.blister-option__price-per-seed {
  font-size: 0.8rem;
}

.blister-option__check {
  font-size: 1.25rem;
  color: var(--primary-color);
  opacity: 0;
  transition: all 0.2s ease;
  margin-left: 1rem;
}

.blister-option.active .blister-option__check {
  opacity: 1;
}

@media (min-width: 768px) {
  .blister-options {
    grid-template-columns: repeat(2, 1fr);
  }
}

.blister-select {
  background: var(--black-color-2);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.form-select {
  background-color: var(--black-color-3);
  color: var(--white-color);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:hover:not(.disabled) {
  border-color: var(--primary-color);
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem rgba(var(--primary-color-rgb), 0.1);
}

.form-select.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-select option:disabled {
  color: var(--text-muted);
  font-style: italic;
}

.stock-badge {
  font-size: 0.875rem;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
}

.stock-badge--low {
  background: rgba(var(--danger-rgb), 0.1);
  color: var(--danger-color);
}

/* Add these styles */
.zoom-container {
  overflow: hidden;
  border-radius: 12px;
  background: linear-gradient(
    to bottom,
    var(--dark-color),
    var(--dark-color-2)
  );
  position: relative;
  cursor: zoom-in;
  width: 100%;
}

.zoom-image {
  width: 100%;
  height: 100%;
  max-height: 600px;
  transition: transform 0.1s ease-out;
  object-fit: cover;
  object-position: top;
  display: block;
}

/* Remove or comment out the hover effect styles if they exist */
/* .zoom-container:hover .zoom-image {
  transform: scale(1.2);
} */

.description-wrapper {
  display: inline;
}

.description-wrapper :deep(p) {
  display: inline;
}
.btn-link {
  font-size: 1rem;
  margin-left: 0.25rem;
  vertical-align: baseline;
}

.thumbnails-wrapper {
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  padding: 0.5rem;
}

.thumbnails-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.thumbnail-btn {
  border: 2px solid transparent;
  border-radius: 8px;
  background: var(--black-color-3);
  cursor: pointer;
  transition: all 0.2s ease;
  width: 150px;
  height: 150px;
  padding: 0.25rem;
  overflow: hidden;
}

.thumbnail-btn:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.thumbnail-btn.active {
  border-color: var(--primary-color);
  background: var(--black-color-2);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.thumbnail-btn:hover .thumbnail-img {
  transform: scale(1.1);
}

/* Ajustes responsive */
@media (max-width: 768px) {
  .thumbnail-btn {
    min-width: 60px;
    height: 60px;
  }
}

/* Actualizar los estilos de las miniaturas */
.thumbnails-row {
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0.5rem;
  max-width: 100%;
}

.thumbnails-row::-webkit-scrollbar {
  display: none;
}

.thumbnail-btn {
  border: 2px solid transparent;
  border-radius: 8px;
  background: var(--black-color-3);
  cursor: pointer;
  transition: all 0.2s ease;
  width: 70px;
  height: 70px;
  padding: 0.25rem;
  overflow: hidden;
  flex-shrink: 0;
}

.thumbnail-btn:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.thumbnail-btn.active {
  border-color: var(--primary-color);
  background: var(--black-color-2);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  transition: transform 0.2s ease;
}

/* Ajustes responsive */
@media (max-width: 768px) {
  .thumbnail-btn {
    width: 60px;
    height: 60px;
  }
}

@media (max-width: 450px) {
  .thumbnail-btn {
    width: 50px;
    height: 50px;
  }
}

/* Ajuste para pantallas muy pequeñas */
@media (max-width: 450px) {
  .thumbnails-column {
    width: 50px;
  }
  
  .thumbnail-btn {
    width: 50px;
    height: 50px;
  }
}

.login-required-section {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

  
.alert-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

/* Estilos para el precio tachado (precio original) */
.old-price {
  text-decoration: line-through;
  color: #b0b0b0;
  font-size: 1rem;
  margin-right: 8px;
}

/* Estilos para el precio con descuento */
.discounted-price {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 1.3rem;
}

/* Badge de descuento */
.discount-badge-wrapper {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
}

.discount-badge {
  background: #ffc107;
  color: #212529;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* Overlay para la imagen al hacer hover */
.img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.08);
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 12px;
}

.image-container:hover .img-overlay {
  opacity: 1;
}

/* Select de blisters */
.blister-select {
  min-width: 80px;
  background: #23272b;
  color: #fff;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 0.95rem;
}

.blister-select.disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Icono de ojo para vista rápida */
.eye-icon {
  font-size: 1.2rem;
  cursor: pointer;
  transition: color 0.2s;
}

.eye-icon:hover {
  color: #0dcaf0;
}

/* Ajustes para el contenedor de la imagen principal */
.product-img-wrapper {
  position: relative;
}

.zoom-container {
  overflow: hidden;
  border-radius: 12px;
  background: #222;
  position: relative;
}

.zoom-image {
  width: 100%;
  transition: transform 0.2s;
  border-radius: 12px;
}

/* Skeleton loaders */
.skeleton-image,
.skeleton-content,
.skeleton-title,
.skeleton-tag,
.skeleton-description,
.skeleton-specific,
.skeleton-price,
.skeleton-button {
  background: var(--black-color-3);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
  border-radius: 8px;
}

.skeleton-image {
  width: 100%;
  height: 320px;
  margin-bottom: 16px;
}

.skeleton-title {
  width: 60%;
  height: 28px;
  margin-bottom: 12px;
}

.skeleton-tags {
  margin-bottom: 12px;
}

.skeleton-tag {
  width: 60px;
  height: 20px;
  margin-right: 8px;
}

.skeleton-description {
  width: 100%;
  height: 60px;
  margin-bottom: 16px;
}

.skeleton-specific {
  width: 60px;
  height: 20px;
}

.skeleton-price {
  width: 40%;
  height: 28px;
  margin-bottom: 12px;
}

.skeleton-button {
  width: 120px;
  height: 36px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Stock indicator styles */
.text-danger {
  color: #dc3545 !important;
}

/* Bank info section */
.brand-info-section {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.brand-logo-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.brand-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.brand-description {
  font-size: 1rem;
  line-height: 1.6;
}
</style>